import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Activity, DollarSign, Users, Zap, AlertTriangle, BarChart3,
  Sliders, RefreshCw, Save, ChevronRight, FileText,
} from "lucide-react";

const TABS = [
  { key: "burn",     label: "Burn rate",   icon: Activity },
  { key: "users",    label: "Heavy users", icon: AlertTriangle },
  { key: "economics", label: "Plan economics", icon: BarChart3 },
  { key: "features", label: "By feature",  icon: Zap },
  { key: "plans",    label: "Plans",       icon: Sliders },
  { key: "jobs",     label: "Background",  icon: FileText },
];

export default function AdminGovernance() {
  const [tab, setTab] = useState("burn");
  return (
    <div data-testid="admin-governance" className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Cost &amp; Plan Governance</p>
          <h2 className="font-heading text-2xl text-cocoa">AI Insight economics.</h2>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 border-b border-champagne pb-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`gov-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${tab === t.key ? "bg-cocoa text-ivory" : "text-cocoa/65 hover:bg-champagne"}`}>
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "burn"      && <BurnRate />}
      {tab === "users"     && <HeavyUsers />}
      {tab === "economics" && <Economics />}
      {tab === "features"  && <ByFeature />}
      {tab === "plans"     && <PlansEditor />}
      {tab === "jobs"      && <BackgroundJobs />}
    </div>
  );
}

function fmtUsd(v) { return `$${(Number(v) || 0).toFixed(v < 1 ? 4 : 2)}`; }

function BurnRate() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/governance/burn-rate", { params: { days } }).then((r) => setData(r.data)).catch(() => setData(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [days]);
  if (!data) return <p className="text-cocoa/55 text-sm">Loading…</p>;
  const maxCost = Math.max(...(data.series || []).map((s) => s.cost_usd), 0.01);
  return (
    <div data-testid="gov-burn-rate" className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Window</span>
        {[7, 14, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`gov-burn-days-${d}`}
            className={`text-xs px-3 py-1 rounded-full ${days === d ? "bg-cocoa text-ivory" : "text-cocoa/70 hover:bg-champagne"}`}>
            {d}d
          </button>
        ))}
        <button onClick={load} className="ml-auto text-xs text-cocoa/60 hover:text-cocoa inline-flex items-center gap-1"><RefreshCw size={12} /> Refresh</button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <KPI label="Total cost" value={fmtUsd(data.total_cost_usd)} icon={DollarSign} />
        <KPI label="Total AI calls" value={data.total_calls.toLocaleString()} icon={Activity} />
        <KPI label="Total tokens" value={data.total_tokens.toLocaleString()} icon={Zap} />
      </div>
      <div className="ss-card p-5">
        <p className="text-[10px] tracking-[0.3em] uppercase text-mauve mb-3">Daily cost ({days}d)</p>
        <div className="flex items-end gap-1 h-32">
          {(data.series || []).map((s) => (
            <div key={s.date} title={`${s.date} · ${fmtUsd(s.cost_usd)} · ${s.calls} calls`}
              className="flex-1 bg-mauve/70 hover:bg-mauve rounded-sm transition-colors min-w-[2px]"
              style={{ height: `${Math.max(2, (s.cost_usd / maxCost) * 100)}%` }} />
          ))}
        </div>
        {data.series.length === 0 && <p className="text-cocoa/55 text-sm text-center py-6">No AI activity in this window yet.</p>}
      </div>
      <CostByUser days={days} />
    </div>
  );
}

function CostByUser({ days = 30 }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/governance/cost-by-user", { params: { days, limit: 25 } }).then((r) => setRows(r.data?.rows || [])).catch(() => setRows([]));
  }, [days]);
  if (!rows.length) return null;
  return (
    <div className="ss-card overflow-hidden">
      <div className="p-4 border-b border-champagne">
        <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Top spenders ({days}d)</p>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-champagne/40 text-cocoa/60 text-[11px] uppercase tracking-wider">
          <tr><th className="text-left p-3">User</th><th className="text-left p-3">Plan</th><th className="text-right p-3">Calls</th><th className="text-right p-3">Units</th><th className="text-right p-3">Cost</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.user_id} className="border-t border-champagne hover:bg-champagne/20">
              <td className="p-3 font-mono text-[12px] truncate max-w-[260px]">{r.email || r.user_id}</td>
              <td className="p-3 text-[12px]"><span className="inline-block px-2 py-0.5 rounded-full bg-champagne/60 text-cocoa">{r.plan}</span></td>
              <td className="p-3 text-right tabular-nums">{r.calls}</td>
              <td className="p-3 text-right tabular-nums">{r.units}</td>
              <td className="p-3 text-right tabular-nums font-medium text-cocoa">{fmtUsd(r.cost_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HeavyUsers() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/governance/heavy-users").then((r) => setRows(r.data?.rows || [])).catch(() => setRows([]));
  }, []);
  return (
    <div data-testid="gov-heavy-users" className="space-y-3">
      <p className="text-[12px] text-cocoa/65">Users above 70% / 90% of monthly entitlement, near or over internal cost cap, or flagged as upgrade candidates.</p>
      {rows.length === 0 ? (
        <div className="ss-card p-6 text-cocoa/55 text-sm text-center">No heavy users this month. Burn rate is healthy.</div>
      ) : (
        <div className="ss-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-champagne/40 text-cocoa/60 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">User</th><th className="text-left p-3">Plan</th>
                <th className="text-right p-3">Units</th><th className="text-right p-3">% used</th>
                <th className="text-right p-3">Cost</th><th className="text-left p-3">Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-t border-champagne hover:bg-champagne/20">
                  <td className="p-3 font-mono text-[12px] truncate max-w-[200px]">{r.email}</td>
                  <td className="p-3 text-[12px]"><span className="inline-block px-2 py-0.5 rounded-full bg-champagne/60">{r.plan}</span></td>
                  <td className="p-3 text-right tabular-nums">{r.units_used} / {r.units_limit}</td>
                  <td className={`p-3 text-right tabular-nums font-medium ${r.pct_units >= 100 ? "text-mauve" : r.pct_units >= 90 ? "text-mauve/80" : "text-cocoa"}`}>{r.pct_units}%</td>
                  <td className="p-3 text-right tabular-nums">{fmtUsd(r.cost_used_usd)}</td>
                  <td className="p-3"><div className="flex flex-wrap gap-1">{r.flags.map((f) => <span key={f} className="text-[10px] uppercase tracking-wide bg-mauve/15 text-mauve px-1.5 py-0.5 rounded">{f.replace(/_/g, " ")}</span>)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Economics() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/governance/economics").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return <p className="text-cocoa/55 text-sm">Loading…</p>;
  return (
    <div data-testid="gov-economics" className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <KPI label="Total users" value={data.totals.users} icon={Users} />
        <KPI label="Monthly recurring (est.)" value={fmtUsd(data.totals.mrr_usd)} icon={DollarSign} />
        <KPI label="30d AI cost" value={fmtUsd(data.totals.ai_cost_30d_usd)} icon={Activity} />
      </div>
      <div className="ss-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-champagne/40 text-cocoa/60 text-[11px] uppercase tracking-wider">
            <tr><th className="text-left p-3">Plan</th><th className="text-right p-3">Users</th><th className="text-right p-3">Price/mo</th><th className="text-right p-3">MRR</th><th className="text-right p-3">30d AI cost</th><th className="text-right p-3">Avg/user</th><th className="text-right p-3">Margin</th></tr>
          </thead>
          <tbody>
            {data.plans.map((p) => (
              <tr key={p.plan_key} className="border-t border-champagne hover:bg-champagne/20">
                <td className="p-3 font-medium text-cocoa">{p.name}</td>
                <td className="p-3 text-right tabular-nums">{p.users}</td>
                <td className="p-3 text-right tabular-nums">{fmtUsd(p.price_monthly_usd)}</td>
                <td className="p-3 text-right tabular-nums">{fmtUsd(p.mrr_usd)}</td>
                <td className="p-3 text-right tabular-nums text-cocoa/70">{fmtUsd(p.ai_cost_30d_usd)}</td>
                <td className="p-3 text-right tabular-nums text-cocoa/70">{fmtUsd(p.avg_cost_per_user_usd)}</td>
                <td className={`p-3 text-right tabular-nums font-medium ${p.estimated_margin_usd < 0 ? "text-mauve" : "text-sage"}`}>{fmtUsd(p.estimated_margin_usd)}{p.margin_pct !== null && <span className="text-cocoa/45 text-[11px] ml-1">({p.margin_pct}%)</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ByFeature() {
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState([]);
  useEffect(() => {
    api.get("/admin/governance/cost-by-feature").then((r) => setRows(r.data?.rows || [])).catch(() => setRows([]));
    api.get("/admin/governance/cost-by-model").then((r) => setModels(r.data?.rows || [])).catch(() => setModels([]));
  }, []);
  return (
    <div data-testid="gov-by-feature" className="grid md:grid-cols-2 gap-4">
      <div className="ss-card overflow-hidden">
        <div className="p-3 border-b border-champagne"><p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Cost by feature (30d)</p></div>
        <table className="w-full text-sm">
          <thead className="text-[11px] text-cocoa/55 uppercase tracking-wider"><tr><th className="text-left p-2">Feature</th><th className="text-right p-2">Calls</th><th className="text-right p-2">Units</th><th className="text-right p-2">Cost</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.feature} className="border-t border-champagne"><td className="p-2 text-cocoa">{r.feature}</td><td className="p-2 text-right tabular-nums">{r.calls}</td><td className="p-2 text-right tabular-nums">{r.units}</td><td className="p-2 text-right tabular-nums font-medium">{fmtUsd(r.cost_usd)}</td></tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="p-4 text-cocoa/55 text-sm text-center">No data yet.</p>}
      </div>
      <div className="ss-card overflow-hidden">
        <div className="p-3 border-b border-champagne"><p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Cost by model (30d)</p></div>
        <table className="w-full text-sm">
          <thead className="text-[11px] text-cocoa/55 uppercase tracking-wider"><tr><th className="text-left p-2">Provider · Model</th><th className="text-right p-2">Calls</th><th className="text-right p-2">Tokens</th><th className="text-right p-2">Cost</th></tr></thead>
          <tbody>
            {models.map((r, i) => (
              <tr key={`${r.provider}-${r.model}-${i}`} className="border-t border-champagne"><td className="p-2 text-[12px]">{r.provider} · {r.model}</td><td className="p-2 text-right tabular-nums">{r.calls}</td><td className="p-2 text-right tabular-nums">{r.tokens.toLocaleString()}</td><td className="p-2 text-right tabular-nums font-medium">{fmtUsd(r.cost_usd)}</td></tr>
            ))}
          </tbody>
        </table>
        {models.length === 0 && <p className="p-4 text-cocoa/55 text-sm text-center">No data yet.</p>}
      </div>
    </div>
  );
}

function PlansEditor() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const load = () => api.get("/admin/governance/plans").then((r) => setPlans(r.data?.plans || []));
  useEffect(() => { load(); }, []);

  const save = async (key, patch) => {
    setSavingKey(key);
    try {
      await api.put(`/admin/governance/plans/${key}`, patch);
      await load();
      setEditing(null);
    } finally { setSavingKey(null); }
  };

  return (
    <div data-testid="gov-plans-editor" className="space-y-3">
      <p className="text-[12px] text-cocoa/65">Edit entitlements live. Changes apply immediately to all users on that plan.</p>
      {plans.map((p) => (
        <div key={p.key} data-testid={`gov-plan-${p.key}`} className="ss-card overflow-hidden">
          <div className="p-4 flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="font-heading text-lg text-cocoa">{p.name} <span className="text-[11px] text-cocoa/45 ml-2 uppercase tracking-wider">{p.key}</span></p>
              <p className="text-[12px] text-cocoa/60">{p.tagline}</p>
              <p className="text-[11px] text-cocoa/55 mt-1">{p.ai_insights_per_month} insights · {p.max_profiles >= 9999 ? "∞" : p.max_profiles} profiles · {p.max_reports_per_month} reports · {fmtUsd(p.price_monthly_usd)}/mo · cost cap {fmtUsd(p.internal_cost_cap_usd)}</p>
            </div>
            <button onClick={() => setEditing(editing === p.key ? null : p.key)} data-testid={`gov-plan-edit-${p.key}`} className="ss-pill h-9 text-xs">{editing === p.key ? "Cancel" : "Edit"} <ChevronRight size={12} /></button>
          </div>
          {editing === p.key && <PlanForm plan={p} onSave={(patch) => save(p.key, patch)} saving={savingKey === p.key} />}
        </div>
      ))}
    </div>
  );
}

function PlanForm({ plan, onSave, saving }) {
  const [f, setF] = useState({
    name: plan.name,
    tagline: plan.tagline || "",
    price_monthly_usd: plan.price_monthly_usd,
    price_annual_usd: plan.price_annual_usd,
    ai_insights_per_month: plan.ai_insights_per_month,
    max_profiles: plan.max_profiles,
    max_reports_per_month: plan.max_reports_per_month,
    max_drafts_per_month: plan.max_drafts_per_month,
    max_checkins_per_month: plan.max_checkins_per_month,
    max_input_chars: plan.max_input_chars,
    internal_cost_cap_usd: plan.internal_cost_cap_usd,
    soft_limit_pct: plan.soft_limit_pct,
    second_soft_limit_pct: plan.second_soft_limit_pct,
    deep_memory_enabled: !!plan.deep_memory_enabled,
    priority_processing: !!plan.priority_processing,
    hard_limit_enabled: !!plan.hard_limit_enabled,
    active: !!plan.active,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const Num = ({ k, label, step = 1 }) => (
    <label className="text-[11px] block">
      <span className="text-cocoa/55 uppercase tracking-wider">{label}</span>
      <input type="number" step={step} value={f[k]} onChange={(e) => set(k, Number(e.target.value))} data-testid={`gov-plan-field-${k}`}
        className="w-full bg-ivory border border-champagne rounded h-8 px-2 mt-1 outline-none focus:border-mauve" />
    </label>
  );
  const Bool = ({ k, label }) => (
    <label className="text-[12px] flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`gov-plan-field-${k}`} />
      <span className="text-cocoa/75">{label}</span>
    </label>
  );
  return (
    <div className="border-t border-champagne p-4 space-y-3 bg-champagne/15">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Num k="price_monthly_usd" label="Price/mo USD" step="0.01" />
        <Num k="price_annual_usd" label="Price/yr USD" step="0.01" />
        <Num k="ai_insights_per_month" label="AI Insights" />
        <Num k="max_profiles" label="Max profiles" />
        <Num k="max_reports_per_month" label="Reports" />
        <Num k="max_drafts_per_month" label="Drafts" />
        <Num k="max_checkins_per_month" label="Check-ins" />
        <Num k="max_input_chars" label="Max input chars" />
        <Num k="internal_cost_cap_usd" label="Cost cap USD" step="0.01" />
        <Num k="soft_limit_pct" label="Soft %" />
        <Num k="second_soft_limit_pct" label="2nd soft %" />
      </div>
      <div className="flex flex-wrap gap-5 pt-2">
        <Bool k="deep_memory_enabled" label="Deep memory" />
        <Bool k="priority_processing" label="Priority processing" />
        <Bool k="hard_limit_enabled" label="Hard limit enforced" />
        <Bool k="active" label="Active" />
      </div>
      <div className="flex justify-end pt-2">
        <button onClick={() => onSave(f)} disabled={saving} data-testid="gov-plan-save"
          className="ss-pill ss-pill-gold h-9 text-xs disabled:opacity-50">
          {saving ? "Saving…" : <><Save size={12} /> Save changes</>}
        </button>
      </div>
    </div>
  );
}

function BackgroundJobs() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/governance/jobs").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return <p className="text-cocoa/55 text-sm">Loading…</p>;
  return (
    <div data-testid="gov-jobs" className="space-y-3">
      <p className="text-[12px] text-cocoa/65">Background AI jobs in last {data.window_days} days. These create platform IP and never count against user advisor capacity.</p>
      <div className="ss-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-champagne/40 text-[11px] text-cocoa/55 uppercase tracking-wider">
            <tr><th className="text-left p-3">Feature</th><th className="text-right p-3">Completed</th><th className="text-right p-3">Failed</th><th className="text-right p-3">Cost</th></tr>
          </thead>
          <tbody>
            {data.jobs.map((j) => (
              <tr key={j.feature} className="border-t border-champagne"><td className="p-3 text-cocoa">{j.feature}</td><td className="p-3 text-right tabular-nums text-sage">{j.completed}</td><td className="p-3 text-right tabular-nums text-mauve">{j.failed}</td><td className="p-3 text-right tabular-nums font-medium">{fmtUsd(j.cost_usd)}</td></tr>
            ))}
            {data.jobs.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-cocoa/55 text-sm">No background jobs in window.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon }) {
  return (
    <div className="ss-card p-4">
      <div className="flex items-center gap-2 text-mauve mb-1">
        <Icon size={12} /><span className="text-[10px] tracking-[0.3em] uppercase">{label}</span>
      </div>
      <p className="font-heading text-xl text-cocoa leading-none">{value}</p>
    </div>
  );
}
